import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { Button, Modal, useToast } from '@qh/shared';
import { ApiError, candidateApi } from '../../lib/apiClient';
import { useAuth } from '../../context/AuthContext';

/**
 * Account / Delete (analysis §5.8) — single-purpose page exposing the self-delete
 * affordance. Recovery (after sign-in detects soft-delete) ships separately on
 * `/recover-account` and was wired in Phase 7-dashboard-b.
 *
 * Delete flow (Phase 7-dashboard-c hotfix):
 *   1. Click "Delete my account" → `DeleteAccountDialog` opens.
 *   2. Candidate types DELETE (case-sensitive) to confirm.
 *   3. Server soft-deletes, revokes sessions, clears cookies.
 *   4. Local AuthContext is reset and the candidate is redirected home with a toast.
 *
 * Earlier the dialog asked for the candidate's password — that path was a dead end
 * for OAuth-only candidates who don't have one. The "type DELETE" gate is universal
 * (the JWT cookie is the auth) and consistent across all sign-in methods.
 */
export function AccountPage() {
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <section>
      <header className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-brand-primary">
          Account
        </h1>
        <p className="text-sm text-brand-muted mt-1">
          Manage your QuantumHire candidate account.
        </p>
      </header>

      <div className="bg-white border border-gray-100 rounded-3xl shadow-sm p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl bg-red-50 text-red-700 flex items-center justify-center shrink-0">
            <ShieldAlert className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-display font-bold text-brand-primary">
              Delete your account
            </h2>
            <p className="text-sm text-brand-muted mt-1 leading-relaxed">
              This will hide your profile, applications, and resumes from companies. You can
              recover your account anytime by signing in again with your email and password —
              there's no time limit.
            </p>
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              className="mt-4 px-5 py-2.5 rounded-xl border border-red-200 text-red-700 font-black text-xs uppercase tracking-widest hover:bg-red-50 transition-colors"
            >
              Delete my account
            </button>
          </div>
        </div>
      </div>

      <DeleteAccountDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} />
    </section>
  );
}

const CONFIRM_PHRASE = 'DELETE';

function DeleteAccountDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const { setCandidate } = useAuth();
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const matchesPhrase = confirmation === CONFIRM_PHRASE;

  const handleClose = () => {
    if (pending) return;
    setConfirmation('');
    setError(null);
    onClose();
  };

  async function handleConfirm() {
    if (!matchesPhrase) {
      setError(`Type ${CONFIRM_PHRASE} to confirm.`);
      return;
    }
    setPending(true);
    setError(null);
    try {
      await candidateApi.account.delete();
      // Backend already cleared cookies. Reset local context and redirect home.
      setCandidate(null);
      toast.success('Your account has been deleted. You can recover it anytime by signing in.');
      navigate('/');
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Could not delete your account. Try again in a moment.');
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Delete your account?"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            loading={pending}
            disabled={pending || !matchesPhrase}
          >
            Delete my account
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <ul className="text-sm text-brand-muted space-y-1.5 list-disc pl-5">
          <li>Hide your profile from all companies</li>
          <li>Hide your applications from companies' funnel views</li>
          <li>Stop all email notifications</li>
          <li>Sign you out immediately</li>
        </ul>
        <p className="text-sm text-brand-muted leading-relaxed">
          Your data isn't erased — sign back in anytime to restore everything (no time limit).
        </p>
        <div>
          <label className="block text-xs font-black uppercase tracking-widest text-brand-muted mb-1.5">
            Type <span className="text-red-700">{CONFIRM_PHRASE}</span> to confirm
          </label>
          <input
            type="text"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            placeholder={CONFIRM_PHRASE}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            disabled={pending}
            className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-bold text-brand-primary placeholder:text-gray-300 disabled:bg-gray-50 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-red-300"
          />
          {error && <p className="mt-2 text-sm text-red-700 font-medium">{error}</p>}
        </div>
      </div>
    </Modal>
  );
}
